import {
	RiAlertLine,
	RiBankCardLine,
	RiCheckLine,
	RiDeleteBinLine,
	RiExternalLinkLine,
	RiMailSendLine,
	RiNotification3Line,
	RiSettingsLine,
	RiTeamLine,
	RiTimeLine,
} from "@remixicon/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { fetchClient } from "@/api/client";
import { $api } from "@/api/query";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { fetchCurrentUser } from "@/lib/auth";
import { isOrgAdmin } from "@/lib/role";

const searchSchema = z.object({
	tab: z.enum(["general", "members", "billing", "notifications"]).optional(),
	status: z.enum(["success", "canceled"]).optional(),
});

export const Route = createFileRoute("/_app/settings")({
	// System page: org members are bounced to the dashboard (same pattern as
	// /admin). Org mutations are OrgAdmin-gated by cavort-auth regardless.
	beforeLoad: async () => {
		const user = await fetchCurrentUser();
		if (!isOrgAdmin(user)) {
			throw redirect({ to: "/dashboard" });
		}
	},
	component: SettingsPage,
	validateSearch: searchSchema,
});

type Tab = "general" | "members" | "billing" | "notifications";

const settingsTabs = [
	{ key: "general" as const, label: "General", icon: RiSettingsLine },
	{ key: "members" as const, label: "Members", icon: RiTeamLine },
	{ key: "billing" as const, label: "Billing", icon: RiBankCardLine },
	{
		key: "notifications" as const,
		label: "Notifications",
		icon: RiNotification3Line,
	},
];

function SettingsPage() {
	const search = useSearch({ from: "/_app/settings" });
	const [tab, setTab] = useState<Tab>(search.tab ?? "general");

	// Show toast for Stripe redirect status
	useEffect(() => {
		if (search.status === "success") {
			toast.success("Subscription updated successfully!");
		} else if (search.status === "canceled") {
			toast.info("Checkout was canceled.");
		}
	}, [search.status]);

	return (
		<div className="space-y-4">
			<div>
				<h1 className="text-2xl font-bold tracking-tight">Settings</h1>
				<p className="text-muted-foreground">Manage your organization settings.</p>
			</div>

			<div className="flex gap-1 border-b overflow-x-auto">
				{settingsTabs.map((t) => (
					<button
						key={t.key}
						type="button"
						onClick={() => setTab(t.key)}
						className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
							tab === t.key
								? "border-primary text-primary"
								: "border-transparent text-muted-foreground hover:text-foreground"
						}`}
					>
						<t.icon className="size-4" />
						{t.label}
					</button>
				))}
			</div>

			{tab === "general" && <GeneralTab />}
			{tab === "members" && <MembersTab />}
			{tab === "billing" && <BillingTab />}
			{tab === "notifications" && <OrgNotificationsTab />}
		</div>
	);
}

// ── General Tab ─────────────────────────────────────────────────────

interface OrgEntry {
	id: string;
	name: string;
	slug: string;
	role: string;
	createdAt: string;
	active: boolean;
}

function GeneralTab() {
	const { data: orgs, isLoading } = useQuery({
		queryKey: ["auth", "organizations"],
		queryFn: async (): Promise<OrgEntry[]> => {
			const res = await fetch("/auth/organizations", { credentials: "include" });
			if (!res.ok) return [];
			const body = (await res.json()) as { organizations: OrgEntry[] };
			return body.organizations ?? [];
		},
	});

	const active = orgs?.find((o) => o.active);

	if (isLoading) {
		return (
			<Card>
				<CardHeader>
					<Skeleton className="h-5 w-40" />
					<Skeleton className="h-4 w-64" />
				</CardHeader>
				<CardContent className="space-y-4">
					<Skeleton className="h-9 w-full max-w-sm" />
				</CardContent>
			</Card>
		);
	}

	return (
		<Card>
			<CardHeader>
				<CardTitle>Organization</CardTitle>
				<CardDescription>
					Read-only for now. Renaming will be re-enabled once cavort-auth ships the endpoint
					(cvrt-gmbh/cavort-auth#5).
				</CardDescription>
			</CardHeader>
			<CardContent className="space-y-4">
				<div className="space-y-2 max-w-sm">
					<Label htmlFor="org-name">Name</Label>
					<Input id="org-name" value={active?.name ?? ""} disabled readOnly />
				</div>
				<div className="space-y-2 max-w-sm">
					<Label htmlFor="org-slug">Slug</Label>
					<Input id="org-slug" value={active?.slug ?? ""} disabled readOnly />
				</div>
			</CardContent>
		</Card>
	);
}

// ── Members Tab ─────────────────────────────────────────────────────

// cavort-auth's GET /auth/org/members returns memberId/userId (camelCase)
// but `joined_at` (snake_case) — the handler hand-writes the JSON. Mirror
// that shape exactly so we don't fight serde drift. Filed upstream as
// cvrt-gmbh/cavort-auth#6 alongside the /auth/me wire-shape bug.
interface MemberRow {
	memberId: string;
	userId: string;
	name: string;
	email: string;
	image: string | null;
	role: string;
	joined_at: string;
}

interface InviteRow {
	id: string;
	email: string;
	role: string;
	expiresAt: string;
}

const membersQueryKey = ["auth", "org", "members"] as const;
const invitesQueryKey = ["auth", "org", "invites"] as const;

function MembersTab() {
	const queryClient = useQueryClient();

	const { data: members, isLoading: membersLoading } = useQuery({
		queryKey: membersQueryKey,
		queryFn: async (): Promise<MemberRow[]> => {
			const res = await fetch("/auth/org/members", { credentials: "include" });
			if (!res.ok) return [];
			const body = (await res.json()) as { members: MemberRow[] };
			return body.members ?? [];
		},
	});

	const { data: invitations } = useQuery({
		queryKey: invitesQueryKey,
		queryFn: async (): Promise<InviteRow[]> => {
			const res = await fetch("/auth/org/invites", { credentials: "include" });
			if (!res.ok) return [];
			const body = (await res.json()) as { invites: InviteRow[] };
			return body.invites ?? [];
		},
	});

	const [inviteEmail, setInviteEmail] = useState("");
	const [inviteRole, setInviteRole] = useState("member");
	const [inviting, setInviting] = useState(false);
	const [deleteId, setDeleteId] = useState<string | null>(null);
	const [deleteName, setDeleteName] = useState("");
	const [cancelInviteId, setCancelInviteId] = useState<string | null>(null);

	async function handleInvite() {
		if (!inviteEmail.trim()) return;
		setInviting(true);
		try {
			const res = await fetch("/auth/org/invites", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				credentials: "include",
				body: JSON.stringify({ email: inviteEmail.trim(), role: inviteRole }),
			});
			if (!res.ok) {
				toast.error("Failed to send invitation");
				return;
			}
			toast.success(`Invitation sent to ${inviteEmail}`);
			setInviteEmail("");
			setInviteRole("member");
			await queryClient.invalidateQueries({ queryKey: invitesQueryKey });
		} finally {
			setInviting(false);
		}
	}

	async function handleUpdateRole(memberId: string, role: string) {
		const res = await fetch(`/auth/org/members/${memberId}/role`, {
			method: "PUT",
			headers: { "Content-Type": "application/json" },
			credentials: "include",
			body: JSON.stringify({ role }),
		});
		if (!res.ok) {
			toast.error("Failed to update role");
			return;
		}
		toast.info("Role updated");
		await queryClient.invalidateQueries({ queryKey: membersQueryKey });
	}

	async function handleRemoveMember() {
		if (!deleteId) return;
		const res = await fetch(`/auth/org/members/${deleteId}`, {
			method: "DELETE",
			credentials: "include",
		});
		if (!res.ok) {
			toast.error("Failed to remove member");
		} else {
			toast.success("Member removed");
			await queryClient.invalidateQueries({ queryKey: membersQueryKey });
		}
		setDeleteId(null);
	}

	async function handleCancelInvitation() {
		if (!cancelInviteId) return;
		const res = await fetch(`/auth/org/invites/${cancelInviteId}`, {
			method: "DELETE",
			credentials: "include",
		});
		if (!res.ok) {
			toast.error("Failed to cancel invitation");
		} else {
			toast.success("Invitation cancelled");
			await queryClient.invalidateQueries({ queryKey: invitesQueryKey });
		}
		setCancelInviteId(null);
	}

	return (
		<div className="space-y-6">
			{/* Invite */}
			<Card>
				<CardHeader>
					<CardTitle>Invite Member</CardTitle>
					<CardDescription>Send an email invitation to join your organization.</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="grid grid-cols-[1fr_8rem_auto] gap-x-3 gap-y-2 items-center max-w-xl">
						<Label htmlFor="invite-email">Email</Label>
						<Label>Role</Label>
						<div />
						<Input
							id="invite-email"
							type="email"
							value={inviteEmail}
							onChange={(e) => setInviteEmail(e.target.value)}
							placeholder="colleague@example.com"
						/>
						<Select value={inviteRole} onValueChange={(v) => v && setInviteRole(v)}>
							<SelectTrigger className="text-sm capitalize">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="member">Member</SelectItem>
								<SelectItem value="admin">Admin</SelectItem>
							</SelectContent>
						</Select>
						<Button onClick={handleInvite} disabled={inviting || !inviteEmail.trim()}>
							<RiMailSendLine className="size-4 mr-1.5" />
							{inviting ? "Sending..." : "Invite"}
						</Button>
					</div>
				</CardContent>
			</Card>

			{/* Active Members */}
			<Card>
				<CardHeader>
					<CardTitle>Members</CardTitle>
					<CardDescription>People who have access to this organization.</CardDescription>
				</CardHeader>
				<CardContent>
					{membersLoading ? (
						<div className="space-y-3">
							{[1, 2, 3].map((i) => (
								<Skeleton key={i} className="h-12 w-full" />
							))}
						</div>
					) : (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>User</TableHead>
									<TableHead>Role</TableHead>
									<TableHead>Joined</TableHead>
									<TableHead className="w-10" />
								</TableRow>
							</TableHeader>
							<TableBody>
								{(members ?? []).map((m) => (
									<TableRow key={m.memberId}>
										<TableCell>
											<div className="flex items-center gap-3">
												<Avatar className="size-8">
													<AvatarFallback>
														{(m.name || m.email).slice(0, 2).toUpperCase()}
													</AvatarFallback>
												</Avatar>
												<div>
													<div className="font-medium">{m.name || m.email}</div>
													<div className="text-xs text-muted-foreground">{m.email}</div>
												</div>
											</div>
										</TableCell>
										<TableCell>
											{m.role === "owner" ? (
												<Badge variant="default">Owner</Badge>
											) : (
												<Select
													value={m.role}
													onValueChange={(v) => v && handleUpdateRole(m.memberId, v)}
												>
													<SelectTrigger size="sm" className="w-32">
														<SelectValue />
													</SelectTrigger>
													<SelectContent>
														<SelectItem value="admin">Admin</SelectItem>
														<SelectItem value="member">Member</SelectItem>
													</SelectContent>
												</Select>
											)}
										</TableCell>
										<TableCell className="text-muted-foreground text-sm">
											{new Date(m.joined_at).toLocaleDateString()}
										</TableCell>
										<TableCell>
											{m.role !== "owner" && (
												<Button
													variant="ghost"
													size="icon"
													className="size-8 text-muted-foreground hover:text-destructive"
													onClick={() => {
														setDeleteId(m.memberId);
														setDeleteName(m.name || m.email);
													}}
												>
													<RiDeleteBinLine className="size-4" />
												</Button>
											)}
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					)}
				</CardContent>
			</Card>

			{/* Pending Invitations */}
			{(invitations ?? []).length > 0 && (
				<Card>
					<CardHeader>
						<CardTitle>Pending Invitations</CardTitle>
					</CardHeader>
					<CardContent>
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Email</TableHead>
									<TableHead>Role</TableHead>
									<TableHead>Expires</TableHead>
									<TableHead className="w-10" />
								</TableRow>
							</TableHeader>
							<TableBody>
								{(invitations ?? []).map((inv) => (
									<TableRow key={inv.id}>
										<TableCell className="font-medium">{inv.email}</TableCell>
										<TableCell>
											<Badge variant="outline">{inv.role}</Badge>
										</TableCell>
										<TableCell className="text-muted-foreground text-sm">
											{new Date(inv.expiresAt).toLocaleDateString()}
										</TableCell>
										<TableCell>
											<Button
												variant="ghost"
												size="icon"
												className="size-8 text-muted-foreground hover:text-destructive"
												onClick={() => setCancelInviteId(inv.id)}
											>
												<RiDeleteBinLine className="size-4" />
											</Button>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</CardContent>
				</Card>
			)}

			{/* Remove member confirmation */}
			<Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Remove member</DialogTitle>
						<DialogDescription>
							Are you sure you want to remove <strong>{deleteName}</strong> from this organization?
							They will lose access immediately.
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="outline" onClick={() => setDeleteId(null)}>
							Cancel
						</Button>
						<Button variant="destructive" onClick={handleRemoveMember}>
							Remove
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Cancel invitation confirmation */}
			<Dialog open={!!cancelInviteId} onOpenChange={() => setCancelInviteId(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Cancel invitation</DialogTitle>
						<DialogDescription>Are you sure you want to cancel this invitation?</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="outline" onClick={() => setCancelInviteId(null)}>
							Keep
						</Button>
						<Button variant="destructive" onClick={handleCancelInvitation}>
							Cancel invitation
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}

// ── Billing Tab ─────────────────────────────────────────────────────

function BillingTab() {
	const { data: subscription, isLoading: subLoading } = $api.useQuery(
		"get",
		"/api/v1/billing/subscription",
	);
	const { data: plans, isLoading: plansLoading } = $api.useQuery("get", "/api/v1/billing/plans");
	const [checkingOut, setCheckingOut] = useState<string | null>(null);
	const [openingPortal, setOpeningPortal] = useState(false);

	async function handleCheckout(priceId: string) {
		setCheckingOut(priceId);
		try {
			const { data, error } = await fetchClient.POST("/api/v1/billing/checkout", {
				body: { priceId },
			});
			if (error || !data) {
				toast.error("Failed to create checkout session");
				return;
			}
			window.location.href = data.url;
		} finally {
			setCheckingOut(null);
		}
	}

	async function handlePortal() {
		setOpeningPortal(true);
		try {
			const { data, error } = await fetchClient.POST("/api/v1/billing/portal");
			if (error || !data) {
				toast.error("Failed to open billing portal");
				return;
			}
			window.location.href = data.url;
		} finally {
			setOpeningPortal(false);
		}
	}

	const statusIcon = (status: string) => {
		switch (status) {
			case "active":
				return <RiCheckLine className="size-4 text-green-500" />;
			case "trialing":
				return <RiTimeLine className="size-4 text-blue-500" />;
			case "past_due":
				return <RiAlertLine className="size-4 text-yellow-500" />;
			default:
				return null;
		}
	};

	const statusLabel = (status: string) => {
		switch (status) {
			case "active":
				return "Active";
			case "trialing":
				return "Trial";
			case "past_due":
				return "Past Due";
			default:
				return status;
		}
	};

	const formatCurrency = (amount: number | null | undefined, currency: string) => {
		if (amount == null) return "Custom";
		return new Intl.NumberFormat("en-US", {
			style: "currency",
			currency: currency.toUpperCase(),
			minimumFractionDigits: 0,
		}).format(amount / 100);
	};

	return (
		<div className="space-y-6">
			{/* Current Subscription */}
			<Card>
				<CardHeader>
					<CardTitle>Current Plan</CardTitle>
					<CardDescription>
						{subscription ? "Your current subscription details." : "You are on the free tier."}
					</CardDescription>
				</CardHeader>
				<CardContent>
					{subLoading ? (
						<div className="space-y-3">
							<Skeleton className="h-5 w-48" />
							<Skeleton className="h-4 w-64" />
							<Skeleton className="h-4 w-40" />
						</div>
					) : subscription ? (
						<div className="space-y-4">
							<div className="flex items-center gap-3">
								<h3 className="text-lg font-semibold">{subscription.planName ?? "Subscription"}</h3>
								<Badge
									variant={subscription.status === "active" ? "default" : "outline"}
									className="flex items-center gap-1"
								>
									{statusIcon(subscription.status)}
									{statusLabel(subscription.status)}
								</Badge>
							</div>

							<div className="grid gap-2 text-sm">
								<div className="flex justify-between max-w-sm">
									<span className="text-muted-foreground">Price</span>
									<span className="font-medium">
										{formatCurrency(subscription.unitAmount, subscription.currency)}/
										{subscription.interval}
										{subscription.quantity > 1 && ` x ${subscription.quantity} seats`}
									</span>
								</div>
								<div className="flex justify-between max-w-sm">
									<span className="text-muted-foreground">Current period</span>
									<span>
										{new Date(subscription.currentPeriodStart).toLocaleDateString()}
										{" — "}
										{new Date(subscription.currentPeriodEnd).toLocaleDateString()}
									</span>
								</div>
								{subscription.trialEnd && (
									<div className="flex justify-between max-w-sm">
										<span className="text-muted-foreground">Trial ends</span>
										<span>{new Date(subscription.trialEnd).toLocaleDateString()}</span>
									</div>
								)}
								{subscription.cancelAtPeriodEnd && (
									<div className="flex justify-between max-w-sm">
										<span className="text-muted-foreground">Cancels at</span>
										<span className="text-destructive">
											{new Date(subscription.currentPeriodEnd).toLocaleDateString()}
										</span>
									</div>
								)}
							</div>

							<Button variant="outline" onClick={handlePortal} disabled={openingPortal}>
								<RiExternalLinkLine className="size-4 mr-1.5" />
								{openingPortal ? "Opening..." : "Manage Subscription"}
							</Button>
						</div>
					) : (
						<div className="space-y-3">
							<p className="text-sm text-muted-foreground">
								No active subscription. Upgrade to unlock more features.
							</p>
						</div>
					)}
				</CardContent>
			</Card>

			{/* Available Plans */}
			<Card>
				<CardHeader>
					<CardTitle>Available Plans</CardTitle>
					<CardDescription>Choose a plan that fits your team.</CardDescription>
				</CardHeader>
				<CardContent>
					{plansLoading ? (
						<div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
							{[1, 2, 3].map((i) => (
								<Skeleton key={i} className="h-40 w-full" />
							))}
						</div>
					) : !plans || plans.length === 0 ? (
						<p className="text-sm text-muted-foreground">
							No plans configured yet. Plans are managed in your Stripe dashboard.
						</p>
					) : (
						<div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
							{plans.map((plan) => {
								const isCurrentPlan = subscription?.stripePriceId === plan.stripePriceId;

								return (
									<div
										key={plan.id}
										className={`rounded-lg border p-4 space-y-3 ${
											isCurrentPlan ? "border-primary bg-primary/5" : "border-border"
										}`}
									>
										<div>
											<h4 className="font-semibold">{plan.name}</h4>
											{plan.maxMembers && (
												<p className="text-xs text-muted-foreground">
													Up to {plan.maxMembers} members
												</p>
											)}
											{plan.storageGb && (
												<p className="text-xs text-muted-foreground">{plan.storageGb} GB storage</p>
											)}
										</div>

										{isCurrentPlan ? (
											<Badge variant="default">Current Plan</Badge>
										) : (
											<Button
												size="sm"
												className="w-full"
												onClick={() => handleCheckout(plan.stripePriceId)}
												disabled={checkingOut === plan.stripePriceId}
											>
												<RiBankCardLine className="size-4 mr-1.5" />
												{checkingOut === plan.stripePriceId
													? "Redirecting..."
													: subscription
														? "Switch Plan"
														: "Subscribe"}
											</Button>
										)}
									</div>
								);
							})}
						</div>
					)}
				</CardContent>
			</Card>
		</div>
	);
}

// ── Org Notifications Tab ───────────────────────────────────────────

interface PreferenceItem {
	type: string;
	label: string;
	category: string;
	inApp: boolean;
	email: boolean;
}

function OrgNotificationsTab() {
	const [preferences, setPreferences] = useState<PreferenceItem[]>([]);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		fetch("/api/v1/notifications/preferences", { credentials: "include" })
			.then((r) => r.json())
			.then((data) => {
				setPreferences(data.preferences ?? []);
				setLoading(false);
			})
			.catch(() => setLoading(false));
	}, []);

	function updatePref(type: string, field: "inApp" | "email", value: boolean) {
		setPreferences((prev) => prev.map((p) => (p.type === type ? { ...p, [field]: value } : p)));
	}

	async function handleSave() {
		setSaving(true);
		try {
			const res = await fetch("/api/v1/notifications/preferences", {
				method: "PUT",
				headers: { "Content-Type": "application/json" },
				credentials: "include",
				body: JSON.stringify({
					preferences: preferences.map((p) => ({
						type: p.type,
						inApp: p.inApp,
						email: p.email,
					})),
				}),
			});
			if (res.ok) {
				const data = await res.json();
				setPreferences(data.preferences ?? []);
				toast.info("Organization notification preferences saved");
			} else {
				toast.error("Failed to save preferences");
			}
		} catch {
			toast.error("Failed to save preferences");
		} finally {
			setSaving(false);
		}
	}

	const categories = [...new Set(preferences.map((p) => p.category))];

	return (
		<Card>
			<CardHeader>
				<CardTitle>Organization Notifications</CardTitle>
				<CardDescription>
					Override your personal notification defaults for this organization. Changes here only
					affect notifications from this org.
				</CardDescription>
			</CardHeader>
			<CardContent>
				{loading ? (
					<div className="space-y-3">
						{[1, 2, 3].map((i) => (
							<Skeleton key={i} className="h-10 w-full" />
						))}
					</div>
				) : (
					<div className="space-y-6">
						{categories.map((cat) => (
							<div key={cat}>
								<h3 className="text-sm font-medium text-muted-foreground mb-3">{cat}</h3>
								<div className="space-y-3">
									{preferences
										.filter((p) => p.category === cat)
										.map((pref) => (
											<div key={pref.type} className="flex items-center justify-between py-2">
												<span className="text-sm">{pref.label}</span>
												<div className="flex items-center gap-6">
													<label className="flex items-center gap-2 text-sm text-muted-foreground">
														<Switch
															checked={pref.inApp}
															onCheckedChange={(v) => updatePref(pref.type, "inApp", v)}
														/>
														In-app
													</label>
													<label className="flex items-center gap-2 text-sm text-muted-foreground">
														<Switch
															checked={pref.email}
															onCheckedChange={(v) => updatePref(pref.type, "email", v)}
														/>
														Email
													</label>
												</div>
											</div>
										))}
								</div>
							</div>
						))}
						<Button onClick={handleSave} disabled={saving}>
							{saving ? "Saving..." : "Save preferences"}
						</Button>
					</div>
				)}
			</CardContent>
		</Card>
	);
}
